import { useEffect, useRef, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import API from "../api";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const nombresMeses = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"
];

const generarColor = (indice) => {
  const colores = [
    "rgba(54, 162, 235, 0.7)",
    "rgba(255, 99, 132, 0.7)",
    "rgba(255, 206, 86, 0.7)",
    "rgba(75, 192, 192, 0.7)",
    "rgba(153, 102, 255, 0.7)",
    "rgba(255, 159, 64, 0.7)",
  ];
  return colores[indice % colores.length];
};

const GraficoVentasPorVendedor = () => {
  const [datos, setDatos] = useState([]);
  const [filtros, setFiltros] = useState({ responsables: [], anios: [], meses: [] });
  const [mostrarDropdown, setMostrarDropdown] = useState({ responsables: false, anios: false, meses: false });
  const [responsables, setResponsables] = useState([]);
  const [aniosDisponibles, setAniosDisponibles] = useState([]);
  const [mesesDisponibles, setMesesDisponibles] = useState([]);

  const refResponsables = useRef();
  const refAnios = useRef();
  const refMeses = useRef();

  const limpiarMonto = (valor) => {
    if (!valor) return 0;
    if (typeof valor !== "string") valor = String(valor);
    const limpio = valor.replace(/[$.\s]/g, "");
    const numero = parseFloat(limpio);
    return isNaN(numero) ? 0 : numero;
  };

  useEffect(() => {
    fetch(`${API}/ventasoficial_vendedor`)
      .then((res) => res.json())
      .then((data) => {
        const transformado = data
          .filter(d => d.FECHA && d.TOTAL)
          .map((d) => {
            const [dia, mesStr, anioStr] = d.FECHA.split("-"); // dd-mm-yyyy
            const anio = parseInt(anioStr);
            const mes = parseInt(mesStr); // 1 a 12

            return {
              anio,
              mes,
              total: limpiarMonto(d.TOTAL),
              cantidad: 1,
              CODVENDEDOR: d.CODVENDEDOR || "Desconocido"
            };
          });

        setDatos(transformado);
        setAniosDisponibles([...new Set(transformado.map(d => d.anio))].sort());
        setMesesDisponibles([...new Set(transformado.map(d => d.mes))].sort());
        setResponsables([...new Set(transformado.map(d => d.CODVENDEDOR))].sort());
      });
  }, []);

  const toggleDropdown = (campo) => {
    setMostrarDropdown((prev) => ({ ...prev, [campo]: !prev[campo] }));
  };

  const toggleValor = (valor, campo) => {
    setFiltros((prev) => {
      const valores = [...prev[campo]];
      if (valores.includes(valor)) {
        return { ...prev, [campo]: valores.filter((v) => v !== valor) };
      } else {
        return { ...prev, [campo]: [...valores, valor] };
      }
    });
  };

  const aplicarFiltros = () => {
    setFiltros({ ...filtros });
  };

  const limpiarFiltros = () => {
    setFiltros({ responsables: [], anios: [], meses: [] });
  };

  const optionsCantidades = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "top" } },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (value) => value.toLocaleString("es-CL") },
      },
    },
  };

  useEffect(() => {
    const cerrarDropdowns = (e) => {
      if (!refResponsables.current?.contains(e.target)) {
        setMostrarDropdown((prev) => ({ ...prev, responsables: false }));
      }
      if (!refAnios.current?.contains(e.target)) {
        setMostrarDropdown((prev) => ({ ...prev, anios: false }));
      }
      if (!refMeses.current?.contains(e.target)) {
        setMostrarDropdown((prev) => ({ ...prev, meses: false }));
      }
    };
    document.addEventListener("mousedown", cerrarDropdowns);
    return () => document.removeEventListener("mousedown", cerrarDropdowns);
  }, []);

  const datosFiltrados = datos.filter(d =>
    (filtros.responsables.length === 0 || filtros.responsables.includes(d.CODVENDEDOR)) &&
    (filtros.anios.length === 0 || filtros.anios.includes(d.anio)) &&
    (filtros.meses.length === 0 || filtros.meses.includes(d.mes))
  );

  const resumenPorAño = {};

  datosFiltrados.forEach(({ anio, mes, total, cantidad }) => {
    if (!resumenPorAño[anio]) {
      resumenPorAño[anio] = { totales: Array(12).fill(0), cantidades: Array(12).fill(0) };
    }
    resumenPorAño[anio].totales[mes - 1] += total;
    resumenPorAño[anio].cantidades[mes - 1] += cantidad;
  });

  const labels = nombresMeses;

  const datasetsTotales = Object.entries(resumenPorAño).map(([anio, datos], index) => ({
    label: `${anio}`,
    data: datos.totales,
    backgroundColor: generarColor(index),
  }));

  const datasetsCantidades = Object.entries(resumenPorAño).map(([anio, datos], index) => ({
    label: `${anio}`,
    data: datos.cantidades,
    backgroundColor: generarColor(index),
  }));

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => value.toLocaleString("es-CL"),
        },
      },
    },
  };

  return (
    <div className="container my-4">
      <div className="bg-light p-4 rounded shadow-sm mb-4">
        <h4 className="mb-4 text-center">Ventas por Vendedor</h4>
        <div className="row g-3 align-items-end">
          {/* RESPONSABLE */}
          <div className="col-md-3 position-relative" ref={refResponsables}>
            <label className="form-label fw-semibold">Responsables:</label>
            <button onClick={() => toggleDropdown("responsables")} className="form-control text-start">
              {filtros.responsables.length > 0 ? `Seleccionados (${filtros.responsables.length})` : "-- Todos --"}
            </button>
            {mostrarDropdown.responsables && (
              <div className="position-absolute bg-white border rounded shadow w-100 mt-1 z-3 overflow-auto" style={{ maxHeight: "200px" }}>
                {responsables.map((r, i) => (
                  <label key={i} className="dropdown-item">
                    <input type="checkbox" className="form-check-input me-2" checked={filtros.responsables.includes(r)} onChange={() => toggleValor(r, "responsables")} />
                    {r}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* AÑOS */}
          <div className="col-md-3 position-relative" ref={refAnios}>
            <label className="form-label fw-semibold">Años:</label>
            <button onClick={() => toggleDropdown("anios")} className="form-control text-start">
              {filtros.anios.length > 0 ? `Seleccionados (${filtros.anios.length})` : "-- Todos --"}
            </button>
            {mostrarDropdown.anios && (
              <div className="position-absolute bg-white border rounded shadow w-100 mt-1 z-3 overflow-auto" style={{ maxHeight: "200px" }}>
                {aniosDisponibles.map((a, i) => (
                  <label key={i} className="dropdown-item">
                    <input type="checkbox" className="form-check-input me-2" checked={filtros.anios.includes(a)} onChange={() => toggleValor(a, "anios")} />
                    {a}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* MESES */}
          <div className="col-md-3 position-relative" ref={refMeses}>
            <label className="form-label fw-semibold">Meses:</label>
            <button onClick={() => toggleDropdown("meses")} className="form-control text-start">
              {filtros.meses.length > 0 ? `Seleccionados (${filtros.meses.length})` : "-- Todos --"}
            </button>
            {mostrarDropdown.meses && (
              <div className="position-absolute bg-white border rounded shadow w-100 mt-1 z-3 overflow-auto" style={{ maxHeight: "200px" }}>
                {mesesDisponibles.map((m, i) => (
                  <label key={i} className="dropdown-item">
                    <input type="checkbox" className="form-check-input me-2" checked={filtros.meses.includes(m)} onChange={() => toggleValor(m, "meses")} />
                    {nombresMeses[m - 1]}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* BOTONES */}
          <div className="col-md-3 d-flex gap-2">
            <button onClick={aplicarFiltros} className="btn btn-primary w-50">Aplicar</button>
            <button onClick={limpiarFiltros} className="btn btn-danger w-50">Limpiar</button>
          </div>
        </div>
      </div>

      {/* GRAFICOS */}
      <div className="bg-white p-4 rounded shadow mb-5">
        <h4 className="text-center mb-4">Ventas por Mes y Año (Monto en $)</h4>
        <div className="w-full max-w-[1200px] h-[300px] mx-auto">
          <Bar data={{ labels, datasets: datasetsTotales }} options={options} />
        </div>
        
        {/* GRAFICO DE CANTIDADES */}
        <h4 className="text-center mt-5 mb-4">Cantidad de Ventas por Mes y Año</h4>
        <div className="w-full max-w-[1200px] h-[300px] mx-auto">
          <Line data={{ labels, datasets: datasetsCantidades }} options={optionsCantidades} />
        </div>
      </div>
    </div>
  );
};

export default GraficoVentasPorVendedor;
